import { IconComponent } from '@mainstage/core-plugin-api';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import ArrowDropUpIcon from '@material-ui/icons/ArrowDropUp';
import clsx from 'clsx';
import { useCallback, useContext, useState } from 'react';
import { resolvePath, useLocation, useResolvedPath } from 'react-router-dom';
import { isLinkActive } from './utils';
import { WbSidebarItemWithSubmenuContext } from './config';
import { WbButton } from '../WbButton';
import { useWbSidebarMobileState } from './WbNavbarMobileStateContext';
import { WbTooltip } from '../WbTooltip';
import { WbLink } from '../WbLink';

const useStyles = makeStyles(
  theme => ({
    item: {
      height: 32,
      width: '100%',
      borderRadius: 4,
      '&:hover': {
        background: theme.palette.bkg.primary,
      },
      display: 'flex',
      alignItems: 'center',
      color: theme.palette.black,
      cursor: 'pointer',
      position: 'relative',
      background: 'none',
      border: 'none',
    },
    button: {
      height: 32,
      width: '100%',
      borderRadius: 4,
      padding: `0px 8px`,
      '&:hover': {
        background: theme.palette.bkg.primary,
      },
      display: 'flex',
      color: theme.palette.black,
      cursor: 'pointer',
      position: 'relative',
      background: 'none',
      border: 'none',
      justifyContent: 'flex-start',
      fontSize: theme.typography.body1.fontSize,
    },
    itemContainer: {
      width: '100%',
      padding: `0px 8px`,
    },
    selected: {
      background: theme.palette.softened.primary,
    },
    label: {
      marginLeft: theme.spacing(1),
      fontSize: theme.typography.body1.fontSize,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      'text-overflow': 'ellipsis',
    },
    subtitle: {
      fontSize: 10,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      'text-overflow': 'ellipsis',
    },
    dropdownArrow: {
      position: 'absolute',
      right: 21,
    },
    dropdown: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'end',
    },
    dropdownItem: {
      width: '100%',
      padding: '10px 0 10px 0',
      '&:hover': {
        background:
          theme.palette.navigation.navItem?.hoverBackground || '#6f6f6f',
        color: theme.palette.navigation.selectedColor,
      },
    },
    textContent: {
      color: theme.palette.navigation.color,
      paddingLeft: theme.spacing(4),
      paddingRight: theme.spacing(1),
      fontSize: theme.typography.body1.fontSize,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      'text-overflow': 'ellipsis',
    },
    icon: {
      marginLeft: theme.spacing(1),
    },
  }),
  { name: 'WitboostSidebarSubmenuItem' },
);

/**
 * Clickable item displayed when submenu item is clicked.
 * title: Text content of item
 * to: Path to navigate to when item is clicked
 *
 * @public
 */
export type WbSidebarSubmenuItemDropdownItem = {
  title: string;
  to: string;
};
/**
 * Holds submenu item content.
 *
 * @remarks
 * title: Text content of submenu item
 * subtitle: A subtitle displayed under the main title
 * to: Path to navigate to when item is clicked
 * icon: Icon displayed on the left of text content
 * dropdownItems: Optional array of dropdown items displayed when submenu item is clicked.
 *
 * @public
 */
export type WbSidebarSubmenuItemProps = {
  title: string;
  subtitle?: string;
  to?: string;
  onClick?: (
    event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>,
  ) => void;
  icon?: IconComponent;
  dropdownItems?: WbSidebarSubmenuItemDropdownItem[];
  exact?: boolean;
  excludedActivationPaths?: string[];
  extraActivationPaths?: string[];
  closeMenu?: boolean;
};

function isButtonItem(props: WbSidebarSubmenuItemProps): boolean {
  return props.to === undefined && props.onClick !== undefined;
}

/**
 * Item used inside a submenu within the sidebar.
 *
 * @public
 */
export const WbSidebarSubmenuItem = (props: WbSidebarSubmenuItemProps) => {
  const {
    title,
    subtitle,
    to,
    onClick,
    icon: Icon,
    dropdownItems,
    exact,
    closeMenu = true,
    extraActivationPaths = [],
    excludedActivationPaths = [],
  } = props;
  const classes = useStyles();
  const { isSubmenuOpen } = useContext(WbSidebarItemWithSubmenuContext);

  const toLocation = useResolvedPath(to ?? '');
  const currentLocation = useLocation();
  let isActive = isLinkActive(
    currentLocation.pathname,
    toLocation.pathname,
    undefined,
    exact,
    extraActivationPaths,
    excludedActivationPaths,
  );

  const { isMobile, setMenuOpen } = useWbSidebarMobileState();

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
      if (isMobile && closeMenu) {
        setMenuOpen(false);
      }
      onClick?.(event);
    },
    [isMobile, onClick, setMenuOpen, closeMenu],
  );

  const [showDropDown, setShowDropDown] = useState(false);
  const handleClickDropdown = () => {
    setShowDropDown(!showDropDown);
  };
  if (dropdownItems !== undefined) {
    dropdownItems.some(item => {
      const resolvedPath = resolvePath(item.to);
      isActive = isLinkActive(
        currentLocation.pathname,
        resolvedPath.pathname,
        undefined,
        exact,
      );
      return isActive;
    });
    return (
      <Box className={classes.itemContainer}>
        <WbTooltip title={title} enterDelay={500} enterNextDelay={500}>
          <Button
            role="button"
            onClick={handleClickDropdown}
            onTouchStart={e => e.stopPropagation()}
            className={clsx(
              classes.item,
              isActive ? classes.selected : undefined,
            )}
          >
            {Icon && (
              <span className={classes.icon}>
                <Icon fontSize="small" />
              </span>
            )}
            <Typography
              variant="subtitle1"
              component="span"
              className={classes.label}
            >
              {title}
              <br />
              {subtitle && (
                <Typography
                  variant="caption"
                  component="span"
                  className={classes.subtitle}
                >
                  {subtitle}
                </Typography>
              )}
            </Typography>
            {showDropDown ? (
              <ArrowDropUpIcon className={classes.dropdownArrow} />
            ) : (
              <ArrowDropDownIcon className={classes.dropdownArrow} />
            )}
          </Button>
        </WbTooltip>
        {dropdownItems && showDropDown && (
          <Box className={classes.dropdown}>
            {dropdownItems.map((object, key) => (
              <WbTooltip
                key={key}
                title={object.title}
                enterDelay={500}
                enterNextDelay={500}
              >
                <WbLink
                  to={object.to}
                  underline="none"
                  className={classes.dropdownItem}
                  onTouchStart={e => e.stopPropagation()}
                  tabIndex={isSubmenuOpen ? 0 : -1}
                >
                  <Typography component="span" className={classes.textContent}>
                    {object.title}
                  </Typography>
                </WbLink>
              </WbTooltip>
            ))}
          </Box>
        )}
      </Box>
    );
  }
  if (isButtonItem(props)) {
    return (
      <Box className={classes.itemContainer}>
        <WbTooltip title={title} enterDelay={500} enterNextDelay={500}>
          <WbButton
            variant="text"
            aria-label={title}
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => handleClick(e)}
            className={classes.button}
            tabIndex={isSubmenuOpen ? 0 : -1}
            icon={Icon && <Icon fontSize="small" />}
            label={
              <Typography
                variant="subtitle1"
                component="span"
                className={classes.label}
              >
                {title}
                <br />
                {subtitle && (
                  <Typography
                    variant="caption"
                    component="span"
                    className={classes.subtitle}
                  >
                    {subtitle}
                  </Typography>
                )}
              </Typography>
            }
          />
        </WbTooltip>
      </Box>
    );
  }

  return (
    <Box className={classes.itemContainer}>
      <WbTooltip title={title} enterDelay={500} enterNextDelay={500}>
        <WbLink
          onClick={(e: React.MouseEvent<HTMLAnchorElement>) => handleClick(e)}
          to={to!}
          underline="none"
          className={clsx(
            classes.item,
            isActive ? classes.selected : undefined,
          )}
          onTouchStart={e => e.stopPropagation()}
          tabIndex={isSubmenuOpen ? 0 : -1}
        >
          {Icon && <Icon fontSize="small" />}
          <Typography
            variant="subtitle1"
            component="span"
            className={classes.label}
          >
            {title}
            <br />
            {subtitle && (
              <Typography
                variant="caption"
                component="span"
                className={classes.subtitle}
              >
                {subtitle}
              </Typography>
            )}
          </Typography>
        </WbLink>
      </WbTooltip>
    </Box>
  );
};
